/**
 * Top-level Upload Wizard orchestrator.
 *
 * Owns all wizard form state. Children are presentational. On submit,
 * fires `useSubmitUpload` (audit-only ingest), invalidates the
 * collection-detail and channel-datasets caches, fires a Sonner success
 * toast with the record count, and calls `onComplete` so the parent
 * page can switch back to the Datasets tab.
 */

import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../../ui/button';
import { useSubmitUpload } from '../hooks/useDatasets';
import { useVersionList } from '../../hooks/useTaxonomy';
import type { ChannelKey } from '../../shared/channels';
import type { Collection } from '../types';
import Step1UploadCsv, { type ParseResult } from './Step1UploadCsv';
import Step2SelectChannel from './Step2SelectChannel';
import Step3Config from './Step3Config';
import Step4Review from './Step4Review';
import WizardStepIndicator, { type WizardStep } from './WizardStepIndicator';

interface Props {
  collection: Collection;
  /** Called after a successful submit so the page can switch back to Datasets. */
  onComplete: () => void;
}

const UploadWizard: React.FC<Props> = ({ collection, onComplete }) => {
  const [step, setStep] = useState<WizardStep>(1);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [channelKey, setChannelKey] = useState<ChannelKey | null>(null);
  const [taxonomyVersionId, setTaxonomyVersionId] = useState<string>('');

  const submitUpload = useSubmitUpload(collection.id);

  // Mirror Step3's liveness check at the wizard level so the Next /
  // Submit button respects the same gate the Alert advertises.
  // Without this, a user could pick an archived version (the Select
  // intentionally lists archived for repeatable evaluation) and
  // sidestep the "Cannot proceed" state. TanStack Query dedupes the
  // request shared with Step3.
  const { data: taxonomyVersions } = useVersionList(collection.taxonomyKey);
  const liveTaxonomyMissing =
    taxonomyVersions !== undefined &&
    !taxonomyVersions.some((v) => v.status === 'live');

  const canAdvance = (() => {
    if (step === 1) return parseResult !== null;
    if (step === 2) return channelKey !== null;
    if (step === 3) return Boolean(taxonomyVersionId) && !liveTaxonomyMissing;
    return true;
  })();

  const handleSubmit = () => {
    if (!parseResult || !channelKey || !taxonomyVersionId || !file) return;
    // Defensive: same gate as `canAdvance` for step 3. Covers the
    // edge case where taxonomy state shifted (rollback in another
    // tab, archived just now) while the user was on step 4.
    if (liveTaxonomyMissing) return;
    submitUpload.mutate(
      {
        collectionId: collection.id,
        channelKey,
        filename: file.name,
        // B17-FE: actual File bytes now travel to the BE via multipart.
        file,
        taxonomyVersionId,
      },
      {
        onSuccess: (response) => {
          // Cache invalidation (incl. annotation queue) happens inside
          // `useSubmitUpload`. The toast count uses the BE's
          // `recordsImported` — the FE's pre-parse `totalCount` is
          // advisory only and can disagree with the server's lenient
          // ingest result (per-row rejections don't fail the upload).
          toast.success(
            `${response.recordsImported.toLocaleString()} records imported and queued for annotation`,
          );
          if (response.recordsRejected > 0) {
            // Cap the per-row sample at 3 (BE returns up to 20; the
            // toast description would be unreadable beyond a handful).
            // The full rejection list lives on the upload-history
            // detail surface (future work).
            const sample = response.sampleErrors
              .slice(0, 3)
              .map((e) => `Row ${e.rowNumber}: ${e.message}`)
              .join(' · ');
            const rowWord =
              response.recordsRejected === 1 ? 'row' : 'rows';
            toast.warning(
              `${response.recordsRejected.toLocaleString()} ${rowWord} rejected`,
              sample ? { description: sample } : undefined,
            );
          }
          onComplete();
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Upload failed.');
        },
      },
    );
  };

  return (
    <div className="space-y-6" data-testid="upload-wizard">
      <WizardStepIndicator step={step} />

      {step === 1 && (
        <Step1UploadCsv
          file={file}
          parseResult={parseResult}
          onFileSelected={(f, r) => {
            setFile(f);
            setParseResult(r);
          }}
          onClear={() => {
            setFile(null);
            setParseResult(null);
          }}
        />
      )}
      {step === 2 && (
        <Step2SelectChannel
          collection={collection}
          channelKey={channelKey}
          onChange={setChannelKey}
        />
      )}
      {step === 3 && (
        <Step3Config
          collection={collection}
          taxonomyVersionId={taxonomyVersionId}
          onTaxonomyVersionIdChange={setTaxonomyVersionId}
        />
      )}
      {step === 4 && channelKey && taxonomyVersionId && (
        <Step4Review
          collection={collection}
          file={file}
          parseResult={parseResult}
          channelKey={channelKey}
          taxonomyVersionId={taxonomyVersionId}
        />
      )}

      <div className="flex justify-between">
        <Button
          variant="secondary"
          size="sm"
          className="h-8 rounded-md border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-xs hover:bg-accent"
          disabled={step === 1 || submitUpload.isPending}
          onClick={() => setStep((s) => Math.max(1, s - 1) as WizardStep)}
          data-testid="upload-wizard-back"
        >
          Back
        </Button>
        {step < 4 ? (
          <Button
            size="sm"
            disabled={!canAdvance}
            onClick={() => setStep((s) => Math.min(4, s + 1) as WizardStep)}
            data-testid="upload-wizard-next"
          >
            Next
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={submitUpload.isPending}
            onClick={handleSubmit}
            data-testid="upload-wizard-submit"
          >
            {submitUpload.isPending ? 'Importing…' : 'Import & Queue for Annotation'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadWizard;
